import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useGetLedgerWalletTransactions } from 'hooks/queries/Ledger/useGetLedgerWalletTransactions'
import { useRewardsPortfoliosQuery } from 'hooks/queries/Rewards/useRewardsPortfoliosQuery'
import { useRewardsProductsQuery } from 'hooks/queries/Rewards/useRewardsProductsQuery'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { TransactionTypes } from 'types/ledger/ledgerWalletTransactions'
import { RewardsEmbeddedTabs } from './components/embedded/RewardsEmbeddedTabs'
import { RewardCompoundedCalculator } from './components/RewardCompoundedCalculator'
import { RewardsAssets } from './components/RewardsAssets'
import { RewardsDashboard } from './components/RewardsDashboard'
import { RewardsNoWallets } from './components/RewardsNoWallets'
import { RewardsPageHeader } from './components/RewardsPageHeader'
import { RewardsSheetProvider } from './context/RewardsSheetProvider'
import useRewardsTracking from './hooks/useRewardsTracking'
import { useRewardsWallets } from './hooks/useRewardsWallets'
import { useTransactionsPagination } from './hooks/useTransactionsPagination'

export const Rewards = () => {
  const { t } = useTranslation()
  const { trackPageView } = useRewardsTracking()
  const { enableEmbeddedRewards } = useFeatureFlags()
  const { accountCapabilities } = useAccountsContext()
  const {
    isEmbeddedCustomerCompanyCapabilityEnabled,
    isEmbeddedCustomerIndividualCapabilityEnabled,
  } = accountCapabilities || {}
  const isEmbeddedPartner =
    isEmbeddedCustomerCompanyCapabilityEnabled || isEmbeddedCustomerIndividualCapabilityEnabled

  useEffect(() => {
    trackPageView('home')
  }, [])

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useTransactionsPagination()

  const { data: transactionsData, isLoading: isLoadingTransactions } =
    useGetLedgerWalletTransactions({
      type: TransactionTypes.REWARDS,
      page,
      size: rowsPerPage,
    })

  const transactions = transactionsData?.content ?? []

  const { paging } = usePaginationPagingPersist(transactionsData?.pagination)
  const showPagination = transactions.length > 0 && !isLoadingTransactions

  const { data: rewardsProductsData, isLoading: isLoadingProducts } = useRewardsProductsQuery()
  const rewardsProducts = rewardsProductsData?.content ?? []
  const rewardsProductCurrencies =
    rewardsProducts.map(product => product?.currency)?.filter(Boolean) ?? []

  const { data: rewardsPortfolios, isLoading: isLoadingPortfolios } = useRewardsPortfoliosQuery()
  const userPortfolios = rewardsPortfolios?.content ?? []
  const hasPortfolios = userPortfolios.length > 0

  const { rewardsWallets, isLoadingWallets } = useRewardsWallets(rewardsProductCurrencies)
  const hasWalletsForRewards = rewardsWallets?.length > 0

  const isLoading = isLoadingProducts || isLoadingPortfolios || isLoadingWallets

  const showEmbeddedTabs = !!enableEmbeddedRewards && isEmbeddedPartner

  if (isLoading) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (!hasWalletsForRewards) {
    return (
      <RewardsSheetProvider userPortfolios={[]} rewardsProducts={[]}>
        <RewardsPageHeader showWithdrawButton={false} showDepositButton={false} />
        <RewardsEmbeddedTabs showEmbeddedTabs={showEmbeddedTabs}>
          <RewardsNoWallets />
        </RewardsEmbeddedTabs>
      </RewardsSheetProvider>
    )
  }

  if (!hasPortfolios) {
    return (
      <RewardsSheetProvider userPortfolios={userPortfolios} rewardsProducts={rewardsProducts}>
        <RewardsPageHeader showWithdrawButton={false} showDepositButton={true} />
        <RewardsEmbeddedTabs showEmbeddedTabs={showEmbeddedTabs}>
          <Box flexCol gap={16}>
            <RewardsAssets title={t('rewards.allocateToStartEarning')} />
            <RewardCompoundedCalculator />
          </Box>
        </RewardsEmbeddedTabs>
      </RewardsSheetProvider>
    )
  }

  return (
    <RewardsSheetProvider userPortfolios={userPortfolios} rewardsProducts={rewardsProducts}>
      <RewardsPageHeader showWithdrawButton={true} showDepositButton={true} />

      <RewardsEmbeddedTabs showEmbeddedTabs={showEmbeddedTabs}>
        <RewardsDashboard
          transactions={transactions}
          isLoadingTransactions={isLoadingTransactions}
          showPagination={showPagination}
          page={page}
          rowsPerPage={rowsPerPage}
          paging={paging}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </RewardsEmbeddedTabs>
    </RewardsSheetProvider>
  )
}
